import * as React from "react";
import styled from "@emotion/styled";
import defaultSRC from "assets/images/default-project-img.svg";
import type { Image } from "../types";
import { Img } from "./Img";

// NOTE: Optimization techniques taken from:
// - https://css-tricks.com/a-guide-to-the-responsive-images-syntax-in-html/#using-srcset
// - https://www.11ty.dev/docs/plugins/image/
// - https://jakearchibald.com/2021/serving-sharp-images-to-high-density-screens/

type FeaturedImageProps = Omit<React.ComponentProps<typeof Img>, "src"> & {
  src?: string | Image;
};

const createImgAttributes = (src?: string | Image) => {
  if (!src) {
    return { src: defaultSRC };
  }
  if (typeof src === "string") {
    return { src };
  }

  let srcSet: string | undefined;
  if (src.url300 || src.url600 || src.url1200) {
    srcSet = [
      src.url300 ? `${src.url300} 300w` : null,
      src.url600 ? `${src.url600} 600w` : null,
      src.url1200 ? `${src.url1200} 1200w` : null,
    ].join(", ");
  }

  return {
    src: src.url1200 ?? src.url,
    srcSet,
    sizes: srcSet ? "150px" : undefined,
  };
};

/**
 * ReviewableTemplate featured image component
 */
function FeaturedImage({
  src,
  entityType,
  className,
  ...props
}: FeaturedImageProps) {
  const [isError, setIsError] = React.useState(false);

  // Build <img> attributes depending on `src` type
  const attrs = createImgAttributes(src);

  // If optimized src doesn't exist for whatever reason, fallback to original src
  const onError: React.ReactEventHandler<HTMLImageElement> = () => {
    // To prevent endless error loop
    if (!isError) setIsError(true);
  };

  return (
    <ImgBackground
      blur={typeof src === "object" ? src.blur : undefined}
      className={className}
      entityType={entityType}
    >
      <Img
        entityType={entityType}
        onError={onError}
        {...attrs}
        src={isError ? defaultSRC : attrs.src}
        {...props}
        aspectRatio="1 / 1"
      />
    </ImgBackground>
  );
}

type WithBlur = { blur?: string };

/**
 * Wrapper div with background blur
 *
 * Extends the above <Img> component to match size, aspect ratio, and border radius but as a simple
 * div. If Image has blur string, use it to show a blurry psuedo element before full img src loads.
 */
const ImgBackground = styled(Img, {
  shouldForwardProp: (propName) =>
    propName !== "blur" && propName !== "entityType",
})<WithBlur>(({ blur, entityType, theme }) => ({
  ...(blur
    ? {
        overflow: "hidden",
        "&:before": {
          backgroundImage: `url("${blur}")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
          borderRadius:
            entityType === "ARTIST"
              ? theme.borderRadius.round
              : theme.borderRadius.sm,
          overflow: "hidden",
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      }
    : {}),
})).withComponent("div");

export { FeaturedImage, ImgBackground };
